import { useEffect } from "react";
import { useDarkMode } from "../../../../../context/ThemeContext";
import CustomAlert from "../../../../../components/base/CustomAlert";
import { useProviderForm } from "./hooks";
import {
  ProviderHeader,
  ProviderList,
  ProviderPagination,
  ProviderFormModal,
  ProviderDetailsModal,
} from "./components";
import type { ThemeClasses } from "./types";

export default function ProvidersSection() {
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const {
    providers,
    meta,
    loading,
    selectedProvider,
    alert,
    isModalOpen,
    modalType,
    showFormModal,
    loadingSubmit,
    isEdit,
    editingId,
    form,
    selectedStatus,
    searchQuery,
    selectedView,
    currentPage,
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleEditClick,
    handleAddClick,
    handleSubmitProvider,
    handleDeleteProvider,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeFormModal,
    hasFormChanges,
  } = useProviderForm();

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const totalPages = meta?.totalPages || 1;

  return (
    <div className="h-full flex flex-col">
      {alert.show && (
        <CustomAlert title={alert.title} text={alert.message} icon={alert.type} />
      )}

      <ProviderHeader
        themeClasses={themeClasses}
        selectedStatus={selectedStatus}
        searchQuery={searchQuery}
        selectedView={selectedView}
        onStatusChange={handleStatusChange}
        onSearchChange={handleSearchChange}
        onViewChange={setSelectedView}
        onAddProvider={handleAddClick}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            <ProviderList
              providers={providers}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              selectedView={selectedView}
              onDetails={handleDetailsClick}
              onEdit={handleEditClick}
              onDelete={handleDeleteProvider}
              editingId={editingId}
            />
            {providers.length > 0 && (
              <ProviderPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {isModalOpen && modalType === "details" && selectedProvider && (
        <ProviderDetailsModal
          provider={selectedProvider}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={closeModal}
          onEdit={handleEditClick}
          onDelete={handleDeleteProvider}
        />
      )}

      {showFormModal && (
        <ProviderFormModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          form={form}
          loadingSubmit={loadingSubmit}
          onClose={closeFormModal}
          onSubmit={handleSubmitProvider}
          isEdit={isEdit}
          hasFormChanges={hasFormChanges}
        />
      )}
    </div>
  );
}
